// src/hooks/useWsMode.js
import { useCallback, useEffect, useMemo, useState } from "react";
import logger from "../utils/logger";

/**
 * 管理 WebSocket 模式：
 * - off: 關閉
 * - test: 測試用（PageHeader toggle）
 * - human: 真人客服（由 window event 觸發）
 * - ai: AI 串流回覆等待中（由 useChat 送出訊息時開啟，收到 done/停止後關閉）
 */
export function useWsMode({ auth, wsEndpoint }) {
  const [wsMode, setWsMode] = useState("off"); // 'off' | 'test' | 'human' | 'ai'

  const tokenForWs = useMemo(() => {
    return auth?.getToken ? auth.getToken() : null;
  }, [auth]);

  const canConnect = useMemo(() => {
    return Boolean(wsEndpoint && tokenForWs);
  }, [wsEndpoint, tokenForWs]);

  const ensureCanConnect = useCallback(() => {
    if (!wsEndpoint) {
      logger.warn("WebSocket", "未設定 REACT_APP_WS_ENDPOINT，無法建立連線");
      return false;
    }
    if (!tokenForWs) {
      logger.warn("WebSocket", "尚未取得 token，請先登入再使用 WebSocket");
      return false;
    }
    return true;
  }, [wsEndpoint, tokenForWs]);

  const toggleTest = useCallback(() => {
    if (!ensureCanConnect()) return;
    setWsMode((prev) => (prev === "test" ? "off" : "test"));
  }, [ensureCanConnect]);

  const enableHuman = useCallback(() => {
    // [DIAG] 記錄 enableHuman 被呼叫時的連線條件
    logger.info("WebSocket[DIAG]", "enableHuman() 被呼叫", {
      hasEndpoint: !!wsEndpoint,
      hasToken: !!tokenForWs,
      canConnect,
    });
    if (!ensureCanConnect()) {
      logger.warn("WebSocket[DIAG]", "enableHuman() 因 ensureCanConnect() 失敗而中止");
      return;
    }
    setWsMode("human");
    logger.info("WebSocket[DIAG]", "wsMode 已設定為 human");
  }, [ensureCanConnect, wsEndpoint, tokenForWs, canConnect]);

  const disableWs = useCallback(() => setWsMode("off"), []);

  // AI 串流等待期間才連線：僅在目前完全沒開（"off"）時才切到 "ai"，
  // 避免蓋掉已存在的 "human"／"test" 連線
  const enableAi = useCallback(() => {
    if (!ensureCanConnect()) return;
    setWsMode((prev) => (prev === "off" ? "ai" : prev));
  }, [ensureCanConnect]);

  // 僅在目前是 "ai" 時才關閉；若期間已被轉真人（"human"）則保留連線
  const disableAi = useCallback(() => {
    setWsMode((prev) => (prev === "ai" ? "off" : prev));
  }, []);

  // ✅ token/endpoint 不可用時，自動關閉（避免卡在 enabled 但連不上）
  useEffect(() => {
    if (!canConnect && wsMode !== "off") {
      logger.warn("WebSocket", "連線條件不足，自動關閉 WebSocket", {
        hasEndpoint: !!wsEndpoint,
        hasToken: !!tokenForWs,
        prevMode: wsMode,
      });
      setWsMode("off");
    } else {
      // [DIAG] 記錄 canConnect 與 wsMode 的組合，方便追蹤
      logger.info("WebSocket[DIAG]", "auto-disable effect 執行（未觸發關閉）", {
        canConnect,
        wsMode,
        hasEndpoint: !!wsEndpoint,
        hasToken: !!tokenForWs,
      });
    }
  }, [canConnect, wsEndpoint, tokenForWs, wsMode]);

  // ✅ 監聽真人客服事件：window.dispatchEvent(new CustomEvent('enableHumanAgentWebSocket', {detail:{...}}))
  useEffect(() => {
    const handler = (event) => {
      logger.info("WebSocket", "收到啟用真人客服 WebSocket 請求", event?.detail);
      enableHuman();
    };

    window.addEventListener("enableHumanAgentWebSocket", handler);
    return () => window.removeEventListener("enableHumanAgentWebSocket", handler);
  }, [enableHuman]);

  return {
    wsMode,
    wsEnabled: wsMode !== "off",
    isHumanMode: wsMode === "human",
    tokenForWs,
    toggleTest,
    enableHuman,
    disableWs,
    enableAi,
    disableAi,
  };
}
